import { useState, useEffect, useCallback } from 'react';
import { Head, Link, router } from '@inertiajs/react';
import { Adventure, AvailableCharacter, formatDuration } from '@/types/adventures';
import { EditorRenderer } from '@/components/Editor/EditorRenderer';
import { CompactHealthBar } from '@/components/Adventures/HealthBar';

/**
 * Props for the Adventure Show page.
 */
interface Props {
    adventure: Adventure;
    characters: AvailableCharacter[];
    statLabels: Record<string, string>;
    skillLabels: Record<string, string>;
}

/**
 * Adventure Show page - displays adventure details with character selection.
 */
export default function AdventureShow({ adventure, characters, statLabels, skillLabels }: Props) {
    const [selectedCharacterIds, setSelectedCharacterIds] = useState<number[]>([]);
    const [successChance, setSuccessChance] = useState<number | null>(null);
    const [isCalculating, setIsCalculating] = useState(false);
    const [isStarting, setIsStarting] = useState(false);

    // Toggle character selection
    const toggleCharacter = (characterId: number) => {
        setSelectedCharacterIds((prev) =>
            prev.includes(characterId)
                ? prev.filter((id) => id !== characterId)
                : [...prev, characterId]
        );
    };

    // Calculate success chance when selection changes
    const calculateSuccessChance = useCallback(async () => {
        if (selectedCharacterIds.length === 0) {
            setSuccessChance(null);
            return;
        }

        setIsCalculating(true);
        try {
            const response = await fetch(`/adventures/${adventure.id}/calculate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                },
                body: JSON.stringify({ character_ids: selectedCharacterIds }),
            });
            const data = await response.json();
            setSuccessChance(data.success_chance);
        } catch (error) {
            console.error('Failed to calculate success chance:', error);
        } finally {
            setIsCalculating(false);
        }
    }, [adventure.id, selectedCharacterIds]);

    // Recalculate when selection changes
    useEffect(() => {
        calculateSuccessChance();
    }, [calculateSuccessChance]);

    // Start the adventure
    const handleStart = () => {
        if (selectedCharacterIds.length === 0) return;

        setIsStarting(true);
        router.post(`/adventures/${adventure.id}/start`, {
            character_ids: selectedCharacterIds,
        }, {
            onFinish: () => setIsStarting(false),
        });
    };

    // Get challenge label
    const getChallengeLabel = (statType: string): string => {
        return statLabels[statType] || skillLabels[statType] || statType;
    };

    return (
        <>
            <Head title={adventure.name} />

            <div className="min-h-screen bg-slate-900 py-8 px-4">
                <div className="max-w-4xl mx-auto">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                        <Link
                            href="/adventures"
                            className="text-gray-400 hover:text-white transition-colors"
                        >
                            ← Back to Adventures
                        </Link>
                    </div>

                    {/* Adventure Info */}
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 mb-6">
                        <h1 className="text-2xl font-bold text-white mb-2">{adventure.name}</h1>

                        {/* Description rendered as Markdown */}
                        {adventure.description && (
                            <div className="text-gray-300 mb-4">
                                <EditorRenderer content={adventure.description} />
                            </div>
                        )}

                        {/* Stats */}
                        <div className="flex flex-wrap gap-4 text-sm">
                            <div className="flex items-center gap-2">
                                <span className="text-gray-500">⚔️ Total Difficulty:</span>
                                <span className="text-white font-medium">{adventure.total_difficulty}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-gray-500">⏱️ Duration:</span>
                                <span className="text-white font-medium">
                                    {formatDuration(adventure.estimated_minutes || 0)}
                                </span>
                            </div>
                            {adventure.is_multi_stage && (
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-500">📊 Stages:</span>
                                    <span className="text-purple-400 font-medium">{adventure.stage_count} stages</span>
                                </div>
                            )}
                        </div>

                        {/* Multi-stage warning */}
                        {adventure.is_multi_stage && (
                            <div className="mt-4 p-3 bg-purple-900/30 border border-purple-700/50 rounded-lg">
                                <p className="text-sm text-purple-200">
                                    <span className="font-medium">Multi-Stage Adventure:</span>
                                    {' '}Characters will take damage based on stage outcomes. Ensure your party is healthy before starting.
                                    Each stage takes 10 minutes, with healing opportunities between stages.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Start Dialogue Section - Narrative/Story Content */}
                    {adventure.dialogue_start && (
                        <div className="bg-gradient-to-br from-purple-900/40 to-slate-800 border border-purple-700/50 rounded-lg p-6 mb-6 relative overflow-hidden">
                            {/* Decorative quote icon */}
                            <div className="absolute top-4 right-4 text-purple-500/20 text-6xl font-serif">"</div>

                            <h2 className="text-lg font-semibold text-purple-300 mb-4 flex items-center gap-2">
                                <span>📜</span>
                                <span>The Story Begins...</span>
                            </h2>

                            {/* Dialogue content with Markdown support */}
                            <div className="relative z-10 text-gray-200 leading-relaxed prose prose-invert prose-purple max-w-none">
                                <EditorRenderer content={adventure.dialogue_start} />
                            </div>
                        </div>
                    )}

                    {/* Challenges Section */}
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 mb-6">
                        <h2 className="text-lg font-semibold text-white mb-4">🎯 Challenges</h2>
                        {adventure.challenges.length === 0 ? (
                            <p className="text-gray-400">No challenges - easy adventure!</p>
                        ) : (
                            <div className="grid gap-3 sm:grid-cols-2">
                                {adventure.challenges.map((challenge) => (
                                    <ChallengeCard
                                        key={challenge.id}
                                        challenge={challenge}
                                        label={getChallengeLabel(challenge.stat_type)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Rewards Section */}
                    <RewardsSection adventure={adventure} />

                    {/* Character Selection */}
                    <CharacterSelection
                        characters={characters}
                        selectedIds={selectedCharacterIds}
                        onToggle={toggleCharacter}
                        challenges={adventure.challenges}
                        statLabels={statLabels}
                        skillLabels={skillLabels}
                    />

                    {/* Start Adventure Button */}
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 mt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                {selectedCharacterIds.length === 0 ? (
                                    <p className="text-gray-400">Select at least one character to begin</p>
                                ) : (
                                    <div className="flex items-center gap-4">
                                        <span className="text-gray-300">
                                            {selectedCharacterIds.length} character{selectedCharacterIds.length !== 1 ? 's' : ''} selected
                                        </span>
                                        {isCalculating ? (
                                            <span className="text-gray-400">Calculating...</span>
                                        ) : successChance !== null && (
                                            <span className={`font-medium ${getSuccessColor(successChance)}`}>
                                                {successChance}% success chance
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                            <button
                                onClick={handleStart}
                                disabled={selectedCharacterIds.length === 0 || isStarting}
                                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                                    selectedCharacterIds.length === 0 || isStarting
                                        ? 'bg-slate-600 text-gray-400 cursor-not-allowed'
                                        : 'bg-purple-600 hover:bg-purple-700 text-white'
                                }`}
                            >
                                {isStarting ? 'Starting...' : '⚔️ Start Adventure'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

/**
 * Get color class based on success chance.
 */
function getSuccessColor(chance: number): string {
    if (chance >= 70) return 'text-green-400';
    if (chance >= 50) return 'text-yellow-400';
    if (chance >= 30) return 'text-orange-400';
    return 'text-red-400';
}

/**
 * ChallengeCard - Displays a single challenge.
 */
function ChallengeCard({ challenge, label }: { challenge: { stat_type: string; difficulty: number }; label: string }) {
    const getDifficultyColor = (difficulty: number): string => {
        if (difficulty >= 50) return 'text-red-400';
        if (difficulty >= 30) return 'text-orange-400';
        if (difficulty >= 15) return 'text-yellow-400';
        return 'text-green-400';
    };

    return (
        <div className="bg-slate-700/50 rounded-lg p-3 flex items-center justify-between">
            <span className="text-gray-300">{label}</span>
            <span className={`font-medium ${getDifficultyColor(challenge.difficulty)}`}>
                {challenge.difficulty}
            </span>
        </div>
    );
}

/**
 * RewardsSection - Displays guaranteed rewards (100% chance only).
 */
function RewardsSection({ adventure }: { adventure: Adventure }) {
    // Filter to only show 100% guaranteed rewards
    const guaranteedRewards = adventure.rewards.filter((r) => r.chance_percent === 100);

    if (guaranteedRewards.length === 0) {
        return null;
    }

    return (
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 mb-6">
            <h2 className="text-lg font-semibold text-white mb-4">🎁 Guaranteed Rewards</h2>
            <div className="grid gap-2">
                {guaranteedRewards.map((reward) => (
                    <div key={reward.id} className="flex items-center justify-between bg-slate-700/50 rounded-lg p-3">
                        <div className="flex items-center gap-2">
                            {/* Display reward type icon */}
                            <span>{reward.reward_type.icon}</span>

                            {/* Item rewards */}
                            {reward.reward_type.value === 'item' && (
                                <>
                                    <span className="text-purple-300">{reward.item_recipe?.name || 'Item'}</span>
                                    {reward.item_rarity && (
                                        <span className="text-xs text-gray-400">({reward.item_rarity.label})</span>
                                    )}
                                </>
                            )}

                            {/* Material rewards */}
                            {reward.reward_type.value === 'material' && (
                                <>
                                    <span className="text-amber-300">{reward.material_type}</span>
                                    <span className="text-gray-400 text-sm">
                                        x{reward.quantity_min === reward.quantity_max
                                            ? reward.quantity_min
                                            : `${reward.quantity_min}-${reward.quantity_max}`}
                                    </span>
                                </>
                            )}

                            {/* XP rewards */}
                            {reward.reward_type.value === 'xp' && (
                                <span className="text-green-300">
                                    {reward.quantity_min === reward.quantity_max
                                        ? `${reward.quantity_min} XP`
                                        : `${reward.quantity_min}-${reward.quantity_max} XP`}
                                </span>
                            )}

                            {/* Blueprint rewards */}
                            {reward.reward_type.value === 'blueprint' && (
                                <>
                                    <span className="text-cyan-300">{reward.item_recipe?.name || 'Blueprint'}</span>
                                    {reward.item_rarity && (
                                        <span className="text-xs text-gray-400">({reward.item_rarity.label})</span>
                                    )}
                                </>
                            )}

                            {/* Reputation rewards */}
                            {reward.reward_type.value === 'reputation' && (
                                <>
                                    <span className="text-blue-300">
                                        {reward.reputation_min === reward.reputation_max
                                            ? `+${reward.reputation_min}`
                                            : `+${reward.reputation_min}-${reward.reputation_max}`} Reputation
                                    </span>
                                    <span className="text-gray-400 text-sm">
                                        with {reward.enclave?.owner_name || reward.enclave?.name || 'Unknown'}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

/**
 * CharacterSelection - Character selection grid.
 */
function CharacterSelection({
    characters,
    selectedIds,
    onToggle,
    challenges,
    statLabels,
    skillLabels,
}: {
    characters: AvailableCharacter[];
    selectedIds: number[];
    onToggle: (id: number) => void;
    challenges: Adventure['challenges'];
    statLabels: Record<string, string>;
    skillLabels: Record<string, string>;
}) {
    // Get unique stat types from challenges for display
    const relevantStatTypes = [...new Set(challenges.map((c) => c.stat_type))];

    if (characters.length === 0) {
        return (
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
                <h2 className="text-lg font-semibold text-white mb-4">👥 Select Characters</h2>
                <p className="text-gray-400">
                    No characters available. All your characters may be on other adventures.
                </p>
                <Link
                    href="/adventures/attempts"
                    className="inline-block mt-4 text-purple-400 hover:text-purple-300"
                >
                    View active adventures →
                </Link>
            </div>
        );
    }

    return (
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-white mb-4">👥 Select Characters</h2>
            <p className="text-gray-400 text-sm mb-4">
                Choose which characters to send on this adventure. More characters = higher success chance.
            </p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {characters.map((character) => (
                    <CharacterCard
                        key={character.id}
                        character={character}
                        isSelected={selectedIds.includes(character.id)}
                        onToggle={() => onToggle(character.id)}
                        relevantStatTypes={relevantStatTypes}
                        statLabels={statLabels}
                        skillLabels={skillLabels}
                    />
                ))}
            </div>
        </div>
    );
}

/**
 * CharacterCard - Selectable character card with relevant stats display.
 */
function CharacterCard({
    character,
    isSelected,
    onToggle,
    relevantStatTypes,
    statLabels,
    skillLabels,
}: {
    character: AvailableCharacter;
    isSelected: boolean;
    onToggle: () => void;
    relevantStatTypes: string[];
    statLabels: Record<string, string>;
    skillLabels: Record<string, string>;
}) {
    /**
     * Get the character's value for a given stat type.
     * Checks stats first, then skills, then equipment bonuses.
     */
    const getStatValue = (statType: string): number => {
        // Check base stats
        if (character.stats && character.stats[statType] !== undefined) {
            return character.stats[statType];
        }
        // Check skills
        if (character.skills && character.skills[statType] !== undefined) {
            return character.skills[statType];
        }
        // Check equipment bonuses
        if (character.equipment_bonuses && character.equipment_bonuses[statType] !== undefined) {
            return character.equipment_bonuses[statType];
        }
        return 0;
    };

    /**
     * Get the display label for a stat type.
     */
    const getStatLabel = (statType: string): string => {
        // Check stat labels first, then skill labels
        if (statLabels[statType]) {
            return statLabels[statType];
        }
        if (skillLabels[statType]) {
            return skillLabels[statType];
        }
        // Fallback: capitalize the stat type
        return statType.charAt(0).toUpperCase() + statType.slice(1).replace(/_/g, ' ');
    };

    /**
     * Get color class based on stat value.
     */
    const getStatColor = (value: number): string => {
        if (value >= 15) return 'text-green-400';
        if (value >= 10) return 'text-yellow-400';
        if (value >= 5) return 'text-orange-400';
        return 'text-red-400';
    };

    const isIncapacitated = character.health?.is_incapacitated ?? false;

    return (
        <button
            onClick={() => !isIncapacitated && onToggle()}
            disabled={isIncapacitated}
            className={`text-left p-4 rounded-lg border-2 transition-all ${
                isIncapacitated
                    ? 'bg-slate-800/50 border-slate-700 opacity-60 cursor-not-allowed'
                    : isSelected
                        ? 'bg-purple-900/30 border-purple-500'
                        : 'bg-slate-700/50 border-slate-600 hover:border-slate-500'
            }`}
        >
            {/* Header: Name and checkbox */}
            <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-white">{character.name}</span>
                <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    isSelected ? 'bg-purple-500 border-purple-500' : 'border-gray-500'
                }`}>
                    {isSelected && <span className="text-white text-xs">✓</span>}
                </span>
            </div>

            {/* Class and Level */}
            <div className="text-sm text-gray-400 mb-2">
                <span className="capitalize">{character.class}</span>
                <span className="mx-1">•</span>
                <span>Level {character.level}</span>
            </div>

            {/* Health Display */}
            {character.health && (
                <div className="mb-3">
                    <CompactHealthBar health={character.health} />
                    {character.health.is_incapacitated && (
                        <div className="text-xs text-red-400 mt-1">
                            Cannot participate while incapacitated
                        </div>
                    )}
                    {!character.health.is_incapacitated && character.health.health_percentage < 30 && (
                        <div className="text-xs text-yellow-400 mt-1">
                            Low health - risk of incapacitation
                        </div>
                    )}
                </div>
            )}

            {/* Relevant Stats for this Adventure */}
            {relevantStatTypes.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-600">
                    {relevantStatTypes.map((statType) => {
                        const value = getStatValue(statType);
                        return (
                            <div
                                key={statType}
                                className="flex items-center gap-1 text-xs bg-slate-800/50 px-2 py-1 rounded"
                                title={`${getStatLabel(statType)}: ${value}`}
                            >
                                <span className="text-gray-400">{getStatLabel(statType)}:</span>
                                <span className={`font-medium ${getStatColor(value)}`}>{value}</span>
                            </div>
                        );
                    })}
                </div>
            )}
        </button>
    );
}

